import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { generatePeriodicAwards, generateAnnualAwards, saveAwards} from '../../../api/AwardsAPI';
import AppBreadCrumb from '../../common/AppBreadCrumb';

// A mapping for award keys to their display titles
const awardTitles = {
    mr_consistent: 'Mr. Consistent',
    the_guest_host: 'The Guest Host',
    orange_cap_holder: 'Orange Cap Holder',
    purple_cap_holder: 'Purple Cap Holder',
    big_hitter: 'Big Hitter',
    boundary_king: 'Boundary King',
    mr_dependable: 'Mr. Dependable',
    the_economist: 'The Economist',
    safe_hands: 'Safe Hands',
    the_stalwart: 'The Stalwart'
};

const AwardCard = ({ title, result }) => {
    let cardClass = 'h-100';
    let cardHeaderClass = '';
    let content;

    if (!result || !result.status) {
        content = <p className="card-text text-muted">No eligible winner found for this period.</p>;
    } else if (result.status === 'already_awarded') {
        cardClass += ' bg-light';
        content = (
            <>
                <h4 className="card-title text-muted">{result.award.user.name}</h4>
                <p className="card-text">Previously awarded on {new Date(result.award.created_at).toLocaleDateString()}</p>
                <span className="badge bg-secondary">Already Awarded</span>
            </>
        );
    } else if (result.status === 'new') {
        cardClass += ' border-primary';
        cardHeaderClass = 'bg-primary text-white';
        content = (
            <>
                <h4 className="card-title">{result.winner.user.name}</h4>
                <p className="card-text">{result.winner.reason}</p>
            </>
        );
    }

    return (
        <div className="col-md-6 col-lg-4 mb-3">
            <div className={`card ${cardClass}`}>
                <div className={`card-header ${cardHeaderClass}`}>{title}</div>
                <div className="card-body text-center">{content}</div>
            </div>
        </div>
    );
};

const AwardsPage = () => {
    const today = new Date().toISOString().split('T')[0];
    const threeMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0];
    
    const [startDate, setStartDate] = useState(threeMonthsAgo);
    const [endDate, setEndDate] = useState(today);
    const [year, setYear] = useState(new Date().getFullYear());

    const [periodicAwards, setPeriodicAwards] = useState(null);
    const [annualAwards, setAnnualAwards] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleGeneratePeriodic = () => {
        setIsLoading(true);
        setPeriodicAwards(null);
        generatePeriodicAwards({ start_date: startDate, end_date: endDate }, response => {
            setIsLoading(false);
            if (response) {
                setPeriodicAwards(response);
            } else {
                Swal.fire('Error', 'Could not fetch award suggestions.', 'error');
            }
        });
    };
    
    const handleGenerateAnnual = () => {
        setIsLoading(true);
        setAnnualAwards(null);
        generateAnnualAwards({ year }, response => {
            setIsLoading(false);
            if (response) {
                setAnnualAwards(response);
            } else {
                Swal.fire('Error', 'Could not fetch annual awards.', 'error');
            }
        });
    };

    const handleSave = () => {
        const awardsToSave = [];

        // Collect new periodic awards
        if (periodicAwards) {
            Object.keys(periodicAwards).forEach(key => {
                const result = periodicAwards[key];
                if (result && result.status === 'new') {
                    awardsToSave.push({
                        user_id: result.winner.user.id,
                        award_type: awardTitles[key],
                        description: result.winner.reason,
                        start_date: startDate,
                        end_date: endDate,
                    });
                }
            });
        }
        
        // Collect new annual awards
        if (annualAwards && annualAwards.the_stalwart && annualAwards.the_stalwart.status === 'new') {
             // The Stalwart can have multiple winners
            annualAwards.the_stalwart.winner.forEach(winnerData => {
                 awardsToSave.push({
                    user_id: winnerData.user.id,
                    award_type: awardTitles.the_stalwart,
                    description: winnerData.reason,
                    start_date: `${year}-01-01`,
                    end_date: `${year}-12-31`,
                });
            });
        }
        
        if (awardsToSave.length === 0) {
            Swal.fire('No New Awards', 'There are no new award suggestions to save.', 'info');
            return;
        }

        setIsLoading(true);
        saveAwards({ awards: awardsToSave }, (response) => {
            setIsLoading(false);
            if (response.message) {
                Swal.fire('Awards Saved!', response.message, 'success');
                // Refresh the suggestions to show their "already_awarded" status
                handleGeneratePeriodic(); 
                handleGenerateAnnual();
            } else {
                 Swal.fire('Error', 'Could not save the awards.', 'error');
            }
        });
    };

    return (
        <main>
            <div className="container-fluid">
                <AppBreadCrumb title="Generate Club Awards" />
                
                <div className="card">
                    <div className="card-header">
                        <h5>Periodic Awards</h5>
                    </div>
                    <div className="card-body">
                        <div className="row align-items-end g-3">
                            <div className="col-md-4">
                                <label className="form-label">Start Date</label>
                                <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">End Date</label>
                                <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <button className="btn btn-primary w-100" onClick={handleGeneratePeriodic} disabled={isLoading}>
                                    {isLoading ? 'Generating...' : 'Generate Periodic Awards'}
                                </button>
                            </div>
                        </div>

                        {periodicAwards && (
                            <div className="row mt-4">
                                {Object.keys(periodicAwards).map(key => (
                                    <AwardCard key={key} title={awardTitles[key]} result={periodicAwards[key]} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card mt-4">
                    <div className="card-header">
                        <h5>Annual Milestone Awards</h5>
                    </div>
                    <div className="card-body">
                        <div className="input-group">
                            <span className="input-group-text">Year</span>
                            <input 
                                type="number" 
                                className="form-control" 
                                value={year} 
                                onChange={e => setYear(e.target.value)}
                            />
                            <button className="btn btn-primary" onClick={handleGenerateAnnual} disabled={isLoading}>
                                {isLoading ? 'Generating...' : `Generate Annual Awards`}
                            </button>
                        </div>
                        {annualAwards && (
                            <div className="row mt-4">
                                <AwardCard title={awardTitles.the_stalwart} result={annualAwards.the_stalwart} />
                            </div>
                        )}
                    </div>
                </div>

                {(periodicAwards || annualAwards) && (
                    <div className="text-end mt-4">
                        <button className="btn btn-success btn-lg" onClick={handleSave} disabled={isLoading}>
                           <i className="fas fa-save me-2"></i> Confirm and Save All New Awards
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
};

export default AwardsPage;